import { Icon } from "../../icons/builder/IconBuilder";
import styled from "@emotion/styled";
import { DialogShape } from "../dialogShape";
import { Button } from "../../button";
import { ReactNode, useState } from "react";
import { level10, level30 } from "../../../other/theme/opacities";

interface IProps {
    icon?: Icon;
    iconColor?: string;
    title: string;
    description?: ReactNode;
    details?: ReactNode;
    detailsShowLabel?: string;
    detailsHideLabel?: string;
    closeCallback: () => void;
    buttons?: ReactNode /** If no buttons are provided, then a single OK button will be shown */;
    testId: string;
}

export const NoticeDialog = (props: IProps) => {
    const [showDetails, setShowDetails] = useState(false);
    const detailsClick = () => {
        setShowDetails(value => !value);
    };
    return (
        <DialogShape closeCallback={props.closeCallback} testId={props.testId}>
            <Title>
                {props.icon && (
                    <IconWrapper>
                        <props.icon color={props.iconColor} />
                    </IconWrapper>
                )}
                <div>{props.title}</div>
            </Title>
            {props.description && (
                <Description>{props.description}</Description>
            )}
            {props.details !== undefined && (
                <>
                    <Details>
                        <ShowDetails onClick={detailsClick}>
                            <div>
                                {showDetails
                                    ? (props.detailsHideLabel ?? "Hide details")
                                    : (props.detailsShowLabel ??
                                      "Show details")}
                            </div>
                        </ShowDetails>
                        <TextPanelWrapper isVisible={showDetails}>
                            <TextPanel>{props.details}</TextPanel>
                        </TextPanelWrapper>
                    </Details>
                </>
            )}
            <FooterContainer>
                {props.buttons !== undefined ? (
                    props.buttons
                ) : (
                    <Button
                        buttonType="neutral"
                        onClick={props.closeCallback}
                        testId="ok"
                    >
                        OK
                    </Button>
                )}
            </FooterContainer>
        </DialogShape>
    );
};

const Title = styled("div")(props => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: props.theme.fontSizes.large,
    fontWeight: "bold"
}));

const IconWrapper = styled("div")({
    display: "inline-block",
    width: 18,
    height: 18,
    marginRight: 8,
    paddingBottom: 1
});

const Description = styled("div")(props => ({
    marginTop: 8,
    fontSize: props.theme.fontSizes.medium,
    textAlign: "center",
    ul: {
        textAlign: "left",
        li: {
            listStyleType: "disc"
        }
    }
}));

const Details = styled("div")({
    marginTop: 14,
    textAlign: "center"
});

const ShowDetails = styled("button")(props => ({
    background: "transparent",
    padding: 0,
    border: 0,
    fontSize: props.theme.fontSizes.medium,
    textDecoration: "underline",
    cursor: "pointer",
    color: props.theme.foreground,
    opacity: 0.5,
    "&:hover": {
        opacity: 0.8
    },
    outline: "none"
}));

const TextPanelWrapper = styled("div")<{ isVisible: boolean }>(props => ({
    overflow: "hidden",
    height: props.isVisible ? 312 : 1,
    width: props.isVisible ? 530 : 330,
    transition: "all 0.2s"
}));

const TextPanel = styled("div")(props => ({
    marginTop: 12,
    border: "1px solid " + props.theme.foreground + level30,
    borderRadius: "3px",
    backgroundColor: props.theme.foreground + level10,
    color: props.theme.foreground,
    textAlign: "left",
    fontSize: props.theme.fontSizes.small,
    height: 300,
    overflowY: "auto",
    padding: "9px 10px 1px 10px"
}));

const FooterContainer = styled("div")({
    marginTop: 20,
    display: "flex",
    justifyContent: "center"
});
